import React, { useState } from 'react';
import { 
  MobileSplash, 
  MobileDiscovery, 
  MobilePropertyDetails, 
  MobileCalculator, 
  MobilePortfolio, 
  MobileConcierge, 
  MobileFilters,
  VantageLogo 
} from '@/components/screens/MobileScreens';
import { 
  DesktopDiscovery, 
  DesktopPropertyDetails, 
  DesktopInvestmentModeling, 
  DesktopPortfolioCommand, 
  DesktopConcierge, 
  DesktopAdvancedFilters, 
  DesktopTrustStructure 
} from '@/components/screens/DesktopScreens';
import { Button } from '@/components/ui/button';

// Screen data for labels
const mobileScreens = [
  { id: 1, name: 'Immersive Splash', component: MobileSplash },
  { id: 2, name: 'Home Discovery', component: MobileDiscovery },
  { id: 3, name: 'Property Details', component: MobilePropertyDetails },
  { id: 4, name: 'Investment Calculator', component: MobileCalculator },
  { id: 5, name: 'Portfolio Dashboard', component: MobilePortfolio },
  { id: 6, name: "Owner's Concierge", component: MobileConcierge },
  { id: 7, name: 'Filter Overlay', component: MobileFilters },
];

const desktopScreens = [
  { id: 8, name: 'Landing / Discovery', component: DesktopDiscovery },
  { id: 9, name: 'Property Details', component: DesktopPropertyDetails },
  { id: 10, name: 'Investment Modeling', component: DesktopInvestmentModeling },
  { id: 11, name: 'Portfolio Command', component: DesktopPortfolioCommand },
  { id: 12, name: 'Concierge & Ownership', component: DesktopConcierge },
  { id: 13, name: 'Advanced Filters', component: DesktopAdvancedFilters },
  { id: 14, name: 'Trust & Structure', component: DesktopTrustStructure },
];

// Mobile Device Frame
const MobileFrame = ({ children, label, number }) => (
  <div className="flex flex-col items-center">
    <div className="mobile-frame relative">
      {/* Notch */}
      <div className="mobile-notch" />
      {/* Screen Content */}
      <div className="absolute inset-0 overflow-hidden rounded-[40px]">
        {children}
      </div>
    </div>
    {/* Label */}
    <div className="mt-4 text-center">
      <span className="text-2xs text-primary font-semibold tracking-wider">{number}</span>
      <h4 className="text-sm text-foreground font-medium mt-1">{label}</h4>
    </div>
  </div>
);

// Desktop Frame
const DesktopFrame = ({ children, label, number }) => (
  <div className="flex flex-col">
    <div className="desktop-frame" style={{ width: '100%', height: '400px' }}>
      <div className="w-full h-full overflow-hidden">
        {children}
      </div>
    </div>
    {/* Label */}
    <div className="mt-4">
      <span className="text-2xs text-primary font-semibold tracking-wider">{number}</span>
      <h4 className="text-sm text-foreground font-medium mt-1">{label}</h4>
    </div>
  </div>
);

// Main Showcase Component
export const VantageShowcase = () => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'interactive'
  const [activeScreen, setActiveScreen] = useState(null);
  
  if (viewMode === 'interactive' && activeScreen) {
    const allScreens = [...mobileScreens, ...desktopScreens];
    const screen = allScreens.find(s => s.id === activeScreen);
    const Component = screen?.component;
    const isMobile = activeScreen <= 7;
    
    return (
      <div className="min-h-screen bg-background">
        {/* Interactive Header */}
        <div className="fixed top-0 left-0 right-0 z-50 glass-heavy border-b border-border/30">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <button 
              onClick={() => setActiveScreen(null)}
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Grid
            </button>
            <VantageLogo />
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setActiveScreen(Math.max(1, activeScreen - 1))}
                disabled={activeScreen === 1}
                className="w-8 h-8 rounded-full glass flex items-center justify-center disabled:opacity-30"
              >
                <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm text-muted-foreground px-2">{activeScreen} / 14</span>
              <button 
                onClick={() => setActiveScreen(Math.min(14, activeScreen + 1))}
                disabled={activeScreen === 14}
                className="w-8 h-8 rounded-full glass flex items-center justify-center disabled:opacity-30"
              >
                <svg className="w-4 h-4 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        {/* Interactive View */}
        <div className="pt-20 pb-8 px-8 flex items-center justify-center min-h-screen">
          {isMobile ? (
            <div className="mobile-frame relative" style={{ transform: 'scale(1.1)' }}>
              <div className="mobile-notch" />
              <div className="absolute inset-0 overflow-hidden rounded-[40px]">
                {Component && <Component />}
              </div>
            </div>
          ) : (
            <div className="desktop-frame w-full max-w-6xl" style={{ height: '700px' }}>
              <div className="w-full h-full overflow-hidden">
                {Component && <Component />}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-heavy border-b border-border/30">
        <div className="max-w-[1800px] mx-auto px-8 py-6 flex items-center justify-between">
          <div>
            <VantageLogo size="lg" />
            <p className="text-xs text-muted-foreground mt-1 tracking-wider">ULTRA-LUXURY FINTECH × PROPTECH</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center glass rounded-lg p-1">
              <button 
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-md text-sm transition-all ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Grid View
              </button>
              <button 
                onClick={() => { setViewMode('interactive'); setActiveScreen(1); }}
                className={`px-4 py-2 rounded-md text-sm transition-all ${viewMode === 'interactive' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                Interactive
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hero Section */}
      <section className="py-16 px-8 text-center border-b border-border/20">
        <span className="text-2xs uppercase tracking-[0.3em] text-primary mb-4 block">PRODUCT ECOSYSTEM</span>
        <h1 className="font-serif text-5xl lg:text-6xl text-foreground mb-4">
          14-Screen Product Ecosystem
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
          A unified cross-platform UI system featuring 7 Mobile iOS screens and 7 expanded Desktop Web screens, 
          architected for high-net-worth investors to manage fractional real estate assets.
        </p>
        <div className="flex items-center justify-center gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">7 Mobile Screens</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">7 Desktop Screens</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-primary" />
            <span className="text-muted-foreground">Glassmorphism Design System</span>
          </div>
        </div>
      </section>
      
      {/* Mobile Screens Section */}
      <section className="py-16 px-8 border-b border-border/20">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="text-2xs uppercase tracking-[0.2em] text-primary mb-2 block">PART 1</span>
              <h2 className="font-serif text-3xl text-foreground">Mobile iOS Screens</h2>
              <p className="text-muted-foreground mt-2">7-step investor journey on mobile</p>
            </div>
            <div className="glass px-4 py-2 rounded-lg">
              <span className="text-sm text-muted-foreground">iPhone 14 Pro • 375×812</span>
            </div>
          </div>
          
          {/* Mobile Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-8">
            {mobileScreens.map((screen) => (
              <div 
                key={screen.id}
                className="cursor-pointer group"
                onClick={() => { setViewMode('interactive'); setActiveScreen(screen.id); }}
              >
                <MobileFrame label={screen.name} number={`0${screen.id}`}>
                  <div className="transform scale-100 group-hover:scale-[1.02] transition-transform duration-300">
                    <screen.component />
                  </div>
                </MobileFrame>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Desktop Screens Section */}
      <section className="py-16 px-8">
        <div className="max-w-[1800px] mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <span className="text-2xs uppercase tracking-[0.2em] text-primary mb-2 block">PART 2</span>
              <h2 className="font-serif text-3xl text-foreground">Desktop Web Experience</h2>
              <p className="text-muted-foreground mt-2">7 expanded screens acting as a capital-allocation command center</p>
            </div>
            <div className="glass px-4 py-2 rounded-lg">
              <span className="text-sm text-muted-foreground">Desktop • 1440×900</span>
            </div>
          </div>
          
          {/* Desktop Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {desktopScreens.map((screen) => (
              <div 
                key={screen.id}
                className="cursor-pointer group"
                onClick={() => { setViewMode('interactive'); setActiveScreen(screen.id); }}
              >
                <DesktopFrame label={screen.name} number={screen.id < 10 ? `0${screen.id}` : `${screen.id}`}>
                  <div className="transform scale-100 group-hover:scale-[1.01] transition-transform duration-300 h-full">
                    <screen.component />
                  </div>
                </DesktopFrame>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-12 px-8 border-t border-border/20">
        <div className="max-w-[1800px] mx-auto flex items-center justify-between">
          <div>
            <VantageLogo size="default" />
            <p className="text-xs text-muted-foreground mt-2">Ultra-Luxury Fractional Real Estate</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Product Ecosystem Showcase
          </p>
        </div>
      </footer>
    </div>
  );
};

export default VantageShowcase;
